// src/hooks/queries/useTickets.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ticketsService } from '@/services/tickets';
import { toast } from '@/components/ui';
import type {
  Ticket,
  TicketFiltros,
  TicketCreate,
  TicketUpdate,
  TicketEstadoUpdate,
  TicketAsignar,
  ComentarioCreate,
  TicketComentario,
  TicketAdjunto,
  TicketHistorial,
} from '@/types';

// === TICKETS ===

export function useTickets(params?: TicketFiltros) {
  return useQuery<Ticket[]>({
    queryKey: ['tickets', params],
    queryFn: () => ticketsService.list(params),
  });
}

export function useTicket(id: string | undefined) {
  return useQuery<Ticket>({
    queryKey: ['tickets', id],
    queryFn: () => ticketsService.get(id!),
    enabled: !!id,
  });
}

export function useCrearTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TicketCreate) => ticketsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      toast.success('Ticket creado correctamente');
    },
  });
}

export function useActualizarTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: TicketUpdate }) =>
      ticketsService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['tickets', id] });
      toast.success('Ticket actualizado');
    },
  });
}

export function useEliminarTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ticketsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      toast.success('Ticket eliminado');
    },
  });
}

export function useCambiarEstadoTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: TicketEstadoUpdate }) =>
      ticketsService.cambiarEstado(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['tickets', id] });
      queryClient.invalidateQueries({ queryKey: ['tickets', id, 'historial'] });
      toast.success('Estado actualizado');
    },
  });
}

export function useAsignarTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: TicketAsignar }) =>
      ticketsService.asignar(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['tickets', id] });
      queryClient.invalidateQueries({ queryKey: ['tickets', id, 'historial'] });
      toast.success('Ticket asignado');
    },
  });
}

// === COMENTARIOS ===

export function useComentarios(ticketId: string | undefined) {
  return useQuery<TicketComentario[]>({
    queryKey: ['tickets', ticketId, 'comentarios'],
    queryFn: () => ticketsService.getComentarios(ticketId!),
    enabled: !!ticketId,
  });
}

export function useCrearComentario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ticketId, data }: { ticketId: string; data: ComentarioCreate }) =>
      ticketsService.createComentario(ticketId, data),
    onSuccess: (_, { ticketId }) => {
      queryClient.invalidateQueries({ queryKey: ['tickets', ticketId, 'comentarios'] });
      queryClient.invalidateQueries({ queryKey: ['tickets', ticketId] });
      toast.success('Comentario agregado');
    },
  });
}

export function useEliminarComentario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ticketId, comentarioId }: { ticketId: string; comentarioId: string }) =>
      ticketsService.deleteComentario(ticketId, comentarioId),
    onSuccess: (_, { ticketId }) => {
      queryClient.invalidateQueries({ queryKey: ['tickets', ticketId, 'comentarios'] });
      queryClient.invalidateQueries({ queryKey: ['tickets', ticketId] });
      toast.success('Comentario eliminado');
    },
  });
}

// === ADJUNTOS ===

export function useAdjuntos(ticketId: string | undefined) {
  return useQuery<TicketAdjunto[]>({
    queryKey: ['tickets', ticketId, 'adjuntos'],
    queryFn: () => ticketsService.getAdjuntos(ticketId!),
    enabled: !!ticketId,
  });
}

export function useSubirAdjunto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ticketId, file }: { ticketId: string; file: File }) =>
      ticketsService.uploadAdjunto(ticketId, file),
    onSuccess: (_, { ticketId }) => {
      queryClient.invalidateQueries({ queryKey: ['tickets', ticketId, 'adjuntos'] });
      queryClient.invalidateQueries({ queryKey: ['tickets', ticketId] });
      toast.success('Archivo subido');
    },
  });
}

export function useEliminarAdjunto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ticketId, adjuntoId }: { ticketId: string; adjuntoId: string }) =>
      ticketsService.deleteAdjunto(ticketId, adjuntoId),
    onSuccess: (_, { ticketId }) => {
      queryClient.invalidateQueries({ queryKey: ['tickets', ticketId, 'adjuntos'] });
      queryClient.invalidateQueries({ queryKey: ['tickets', ticketId] });
      toast.success('Archivo eliminado');
    },
  });
}

// === HISTORIAL ===

export function useHistorial(ticketId: string | undefined) {
  return useQuery<TicketHistorial[]>({
    queryKey: ['tickets', ticketId, 'historial'],
    queryFn: () => ticketsService.getHistorial(ticketId!),
    enabled: !!ticketId,
  });
}
